import React from 'react';

import Avatar, {
	type AppearanceType,
	AVATAR_SIZES,
	type AvatarPropTypes,
	type SizeType,
	Skeleton,
} from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import { MediaImage } from '@atlaskit/media-image';
import type { MediaImageWithMediaClientConfigProps } from '@atlaskit/media-image/internal-types';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import Tag from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';

import ColoredTag from './ColoredTag';
import Link from './Link';

export type TagItemMediaImageProps = Omit<MediaImageWithMediaClientConfigProps, 'children'>;

type TagItemIconProps = {
	mediaImage?: TagItemMediaImageProps;
	url?: string;
};

type Props = {
	color?: string | null;
	href?: string;
	icon?: TagItemIconProps;
	isColored?: boolean;
	isOnlyOneItem?: boolean;
	name: string;
	openLinksInNewTab?: boolean;
	shouldDisplayStrikethrough?: boolean;
};

const AVATAR_SIZE: SizeType = 'xxsmall';
const AVATAR_APPEARANCE: AppearanceType = 'square';

const styles = cssMap({
	tagWrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		marginTop: token('space.negative.050'),
		marginBottom: token('space.negative.050'),
		marginRight: token('space.negative.050'),
		marginLeft: token('space.negative.050'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		width: 'max-content',
		maxWidth: '180px',
	},
	truncatedText: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		display: 'inline-block',
		maxWidth: '100%',
	},
});

const TagItemAvatar = ({
	src,
	testId = 'list-with-popup.ui.tag-item.avatar',
}: Pick<AvatarPropTypes, 'src' | 'testId'>) => (
	<Avatar
		appearance={AVATAR_APPEARANCE}
		size={AVATAR_SIZE}
		src={src}
		borderColor="transparent"
		testId={testId}
	/>
);

const TagItemMediaImage = ({
	mediaImage,
	fallbackSrc,
}: {
	fallbackSrc?: string;
	mediaImage: TagItemMediaImageProps;
}) => (
	<MediaImage
		identifier={mediaImage.identifier}
		mediaClientConfig={mediaImage.mediaClientConfig}
		apiConfig={{
			...mediaImage.apiConfig,
			width: AVATAR_SIZES[AVATAR_SIZE],
			height: AVATAR_SIZES[AVATAR_SIZE],
		}}
	>
		{({ loading, error, data }) => {
			if (loading) {
				return <Skeleton appearance={AVATAR_APPEARANCE} size={AVATAR_SIZE} />;
			}

			if (error || !data?.src) {
				return (
					<TagItemAvatar src={fallbackSrc} testId="list-with-popup.ui.tag-item.media-image-error" />
				);
			}

			return (
				<TagItemAvatar src={data?.src} testId="list-with-popup.ui.tag-item.media-image-avatar" />
			);
		}}
	</MediaImage>
);

const TagItemIcon = ({ icon }: { icon: TagItemIconProps }) => (
	<>
		{icon?.mediaImage ? (
			<TagItemMediaImage mediaImage={icon.mediaImage} fallbackSrc={icon.url} />
		) : (
			<TagItemAvatar src={icon?.url} />
		)}
	</>
);

export const TagItem = ({
	name,
	href,
	icon,
	shouldDisplayStrikethrough,
	isColored,
	color,
	openLinksInNewTab,
}: Props): React.JSX.Element => {
	const LinkWithProps = ({ children, ...props }: any) => (
		<Link
			{...props}
			shouldDisplayStrikethrough={shouldDisplayStrikethrough}
			openLinksInNewTab={openLinksInNewTab}
		>
			{children}
		</Link>
	);
	const baseTag = (
		<Box as="span" testId="list-with-popup.ui.tag-item.tag-item" xcss={styles.tagWrapper}>
			<Tag
				elemBefore={icon ? <TagItemIcon icon={icon} /> : undefined}
				text={name}
				href={href}
				linkComponent={LinkWithProps}
				isRemovable={false}
			/>
		</Box>
	);
	const tagNode =
		isColored && color ? (
			<Box as="span" testId="list-with-popup.ui.tag-item.tag-item" xcss={styles.tagWrapper}>
				<ColoredTag
					labelName={name}
					href={href ?? undefined}
					color={color}
					elemBefore={icon ? <TagItemIcon icon={icon} /> : undefined}
					contentXcss={styles.truncatedText}
					openLinksInNewTab={openLinksInNewTab}
				/>
			</Box>
		) : (
			baseTag
		);
	return tagNode;
};

export const TagItemPopupContentWrapper = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => (
	<Inline space="space.100" shouldWrap>
		{children}
	</Inline>
);
